import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Plus, Briefcase, DollarSign, Check, X } from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-yellow-500/10 text-yellow-400',
  closed: 'bg-green-500/10 text-green-400',
  cancelled: 'bg-red-500/10 text-red-400',
};

export default function Deals() {
  const { user } = useAuth();
  const isAdmin = ['admin','toplevman'].includes(user?.role);
  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ lead_id: '', property_id: '', deal_value: '', notes: '' });

  const load = useCallback(async () => {
    try {
      const [dealRes, leadRes, propRes] = await Promise.all([
        api.get('/api/deals'),
        api.get('/api/leads?limit=200'),
        api.get('/api/properties?limit=200')
      ]);
      setDeals(dealRes.data.deals);
      setTotal(dealRes.data.total);
      setLeads(leadRes.data.leads);
      setProperties(propRes.data.properties);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...form, deal_value: parseFloat(form.deal_value) || 0 };
    try {
      await api.post('/api/deals', data);
      setShowForm(false); setForm({ lead_id: '', property_id: '', deal_value: '', notes: '' }); load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleClose = async (id) => {
    if (!window.confirm('Mark this deal as CLOSED?')) return;
    try { await api.put(`/api/deals/${id}`, { status: 'closed' }); load(); }
    catch (err) { alert('Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently archive this deal? This cannot be undone easily.')) return;
    try { await api.delete(`/api/deals/${id}`); load(); }
    catch(e) { alert('Error archiving deal'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this deal?')) return;
    try { await api.put(`/api/deals/${id}`, { status: 'cancelled' }); load(); }
    catch (err) { alert('Error'); }
  };

  const selectProperty = (propId) => {
    const prop = properties.find(p => p.id === propId);
    setForm({ ...form, property_id: propId, deal_value: prop?.value?.toString() || form.deal_value });
  };

  const getCommissionTimer = (deal) => {
    if (!deal.commission_due_date) return null;
    const due = new Date(deal.commission_due_date);
    const now = new Date();
    const daysLeft = Math.ceil((due - now) / 86400000);
    if (daysLeft < 0) return { label: 'Overdue by ' + Math.abs(daysLeft) + 'd', color: 'text-red-400' };
    if (daysLeft < 30) return { label: daysLeft + ' days left', color: 'text-orange-400' };
    if (daysLeft < 90) return { label: Math.ceil(daysLeft/30) + ' months left', color: 'text-yellow-400' };
    return { label: Math.ceil(daysLeft/30) + ' months left', color: 'text-green-400' };
  };

  const totalValue = deals.filter(d => d.status === 'closed').reduce((s, d) => s + (d.deal_value || 0), 0);
  const totalCommission = deals.filter(d => d.status === 'closed').reduce((s, d) => s + (d.commission_amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div><h3 className="text-xl font-bold text-white">Deals ({total})</h3><p className="text-dark-400 text-sm">Track deals and commissions</p></div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg text-sm"><Plus className="w-4 h-4" />New Deal</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="glass rounded-xl p-4"><div className="text-2xl font-bold text-white">AED {totalValue.toLocaleString()}</div><div className="text-sm text-dark-400">Closed Deal Value</div></div>
        <div className="glass rounded-xl p-4"><div className="text-2xl font-bold text-green-400">AED {totalCommission.toLocaleString()}</div><div className="text-sm text-dark-400">Total Commission</div></div>
        <div className="glass rounded-xl p-4"><div className="text-2xl font-bold text-white">{deals.filter(d => d.status === 'closed').length}</div><div className="text-sm text-dark-400">Closed Deals</div></div>
      </div>

      {loading ? <div className="flex justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div></div> :
      deals.length === 0 ? <div className="glass rounded-xl p-8 text-center"><Briefcase className="w-12 h-12 text-dark-500 mx-auto mb-3" /><p className="text-dark-400">No deals yet</p></div> :
      <div className="space-y-3">
        {deals.map(d => (
          <div key={d.id} className="glass rounded-xl p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-white font-medium">{d.property_name || 'Property'}</h4>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[d.status] || ''}`}>{d.status}</span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-dark-400">
                  <span>Lead: {d.lead_name}</span>
                  <span>Agent: {d.agent_name}</span>
                  <span className="text-white font-medium">AED {d.deal_value?.toLocaleString()}</span>
                  <span className="text-green-400">Commission: AED {d.commission_amount?.toLocaleString()} ({d.commission_percentage}%)</span>
                  <span>{new Date(d.created_at).toLocaleDateString()}</span>
                  {(() => { const t = getCommissionTimer(d); return t ? <span className={`font-medium ${t.color}`}>⏱ Commission due: {t.label}</span> : null; })()}
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  {d.status === 'pending' && <>
                    <button onClick={() => handleClose(d.id)} className="px-3 py-1.5 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded text-xs flex items-center gap-1"><Check className="w-3 h-3" />Close Deal</button>
                    <button onClick={() => handleCancel(d.id)} className="px-3 py-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded text-xs flex items-center gap-1"><X className="w-3 h-3" />Cancel</button>
                  </>}
                  <button onClick={() => handleDelete(d.id)} className="px-3 py-1.5 bg-dark-700 text-dark-300 hover:bg-dark-600 rounded text-xs flex items-center gap-1"><X className="w-3 h-3" />Delete</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>}

      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-dark-700">
              <h3 className="text-lg font-semibold text-white">New Deal</h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-dark-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div><label className="block text-sm text-dark-300 mb-1">Lead *</label><select value={form.lead_id} onChange={e => setForm({...form, lead_id: e.target.value})} required className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm"><option value="">Select Lead</option>{leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
              <div><label className="block text-sm text-dark-300 mb-1">Property *</label><select value={form.property_id} onChange={e => selectProperty(e.target.value)} required className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm"><option value="">Select Property</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name} - AED {p.value?.toLocaleString()}</option>)}</select></div>
              <div><label className="block text-sm text-dark-300 mb-1">Deal Value (AED) *</label><input type="number" value={form.deal_value} onChange={e => setForm({...form, deal_value: e.target.value})} required className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
              <div><label className="block text-sm text-dark-300 mb-1">Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm resize-none h-16" /></div>
              <div className="flex gap-3"><button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 bg-dark-800 text-dark-300 rounded-lg text-sm">Cancel</button><button type="submit" className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 rounded-lg text-sm font-medium">Create Deal</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
